/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 11/07/2019
 *
 */

import React from 'react';
import InputTextField from "./inputTextField/inputTextField";
import Icon from "../../icon/Icon";
import DropdownSelect from "./dropDown/dropdownSelect";
import DateTimePicker from "./datePicker/dateTimePicker";
import DropdownMultiSelect from "./dropDown/dropdownMultiSelect";
import Checkbox from "./checkbox/checkbox";
import TextAreaField from "./textArea/textAreaField";
import DateOnlyPicker from "./datePicker/dateOnlyPicker";

/** functional component for FormElementGenerator **/
const FormElementGenerator = ({currentState, handleChange}) => {
    const elements = [];

    currentState.fields.forEach(field => {
        if (field.inputType === 'text' || field.inputType === 'textUrl' || field.inputType === 'textPassword') {
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <InputTextField
                        inputType={field.inputType}
                        labelName={field.labelName}
                        inputName={field.inputName}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={currentState[field.inputName]}
                        handleChange={handleChange}
                        isDisabled={field.disabled}
                        style={field.style || {}} // Apply inline style
                    />

                    {currentState.errors[field.inputName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.inputName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        }else if (field.inputType === 'tpiAuditText') {
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <InputTextField
                        inputType={field.inputType}
                        labelName={field.labelName}
                        inputName={field.inputName}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={currentState[field.inputName]}
                        handleChange={handleChange}
                        isDisabled={field.disabled}
                    />

                    {currentState.errors[field.inputName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.inputName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        } else if (field.inputType === 'datetime-local') { // New case for datetime-local
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <label>{field.labelName}</label>
                    <input
                        type="datetime-local" // Keep as datetime-local
                        name={field.inputName}
                        value={currentState[field.inputName] || ''}
                        onChange={(event) => {
                            const dateTimeValue = event.target.value;
                            // Check if time is missing, append default time
                            const [date, time] = dateTimeValue.split('T');
                            const updatedValue = time ? dateTimeValue : `${date}T00:00:00`;

                            // Set the input value directly in the state
                            handleChange({
                                currentTarget: {
                                    name: field.inputName,
                                    value: updatedValue,
                                },
                            });
                        }}
                    />

                    {currentState.errors[field.inputName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.inputName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        } else if (field.inputType === 'textArea') {
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <TextAreaField
                        labelName={field.labelName}
                        inputName={field.inputName}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={currentState[field.inputName]}
                        handleChange={handleChange}
                        isDisabled={field.disabled}
                    />

                    {currentState.errors[field.inputName] &&
                        <div className="error-msg-container">
                            <Icon name="warning-18" />
                            <span className="error-msg">{currentState.errors[field.inputName]}</span>
                        </div>
                    }
                </fieldset>
            </fieldset>)
        } else if (field.inputType === 'dateTime') {
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <DateTimePicker
                        labelName={field.labelName}
                        optionValueName={field.optionValueName}
                        handleChange={handleChange}
                        valueType={field.valueType}
                        syncInitialValue={field.syncInitialValue !== false}
                    />

                    {currentState.errors[field.inputName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.inputName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        } else if (field.inputType === 'dateOnlyPicker') {
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <DateOnlyPicker
                        labelName={field.labelName}
                        optionValueName={field.optionValueName}
                        handleChange={handleChange}
                        valueType={field.valueType}
                    />

                    {currentState.errors[field.inputName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.inputName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        } else if (field.inputType === 'multiDropdown') {
            const vales = [];

            if (currentState.accountAdminRoles !== undefined && currentState.accountAdminRoles.length !== 0) {
                currentState.accountAdminRoles.forEach(role => {
                    vales.push({
                        value: role.accountAdminRoleId,
                        label: role.adminRoleAuthority
                    })
                });
            }

            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <DropdownMultiSelect
                        labelName={field.labelName}
                        valueType={field.valueType}
                        value={vales}
                        path={field.path}
                        handleChange={handleChange}
                        optionValueName={field.optionValueName}
                        optionLabelName={field.optionLabelName}
                    />

                    {currentState.errors[field.optionValueName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.optionValueName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        } else if (field.inputType === 'checkbox') {
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <div style={{display: 'flex'}}>
                        <label>
                            {field.labelName}
                        </label>
                        <Checkbox name={field.inputName} checked={currentState.accountEnabled} onChange={handleChange} isDisabled={field.disabled}/>
                    </div>
                </fieldset>
            </fieldset>)
        } else {
            let value;
            if (field.valueType === 'basic') {
                value = (currentState[field.inputName]) ?
                    {value: true, label: 'Yes'} : {value: false, label: 'No'};
            } else {
                value = {
                    value: currentState[field.optionValueName],
                    label: currentState[field.optionLabelName]
                }
            }
            elements.push(<fieldset key={field.inputName} className="fieldParent">
                <fieldset>
                    <DropdownSelect
                        labelName={field.labelName}
                        valueType={field.valueType}
                        value={value}
                        path={field.path}
                        handleChange={handleChange}
                        optionValueName={field.optionValueName}
                        optionLabelName={field.optionLabelName}
                        partnerFilterValue={field.partnerFilterStateKey
                            ? currentState[field.partnerFilterStateKey]
                            : ''}
                    />

                    {currentState.errors[field.optionValueName] &&
                    <div className="error-msg-container">
                        <Icon name="warning-18" />
                        <span className="error-msg">{currentState.errors[field.optionValueName]}</span>
                    </div>
                    }
                </fieldset>
            </fieldset>)
        }
    });

    return (elements);
};

export default FormElementGenerator
